/**
 * Pricing Guide - English Content
 * ================================
 * Single source of truth for all translatable strings.
 * The backend mirrors this in services/guide_content.py.
 * Keys are camelCase. Technical terms (SAP HANA, VPC, MFA etc.)
 * are intentionally in English and will not be translated.
 */
export const EN_CONTENT = {
  // Page header
  pageTitle: "Partner Pricing Guide",
  pageSubtitle: "Filter by region and currency, then download a personalised PDF for your partner.",
  regionLabel: "Region",
  currencyLabel: "Currency",
  downloadBtn: "Download PDF",
  translatedNote: "Translated from English - contact your PSM for the authoritative version.",

  // Section 1
  s1Title: "1. The Four Products at a Glance",
  s1Intro: "Four hosted cloud products on AWS infrastructure. Standard, Advanced and MTPro are for SAP Business One. C4P Custom is fully bespoke for any workload.",
  stdTag: "Flat Rate",
  stdIntro: "A simple flat-rate subscription - the same per-user price regardless of how many users you have. Ideal for smaller deployments with predictable monthly billing. No minimum user count.",
  stdF1: "Flat PUPM - any user count",
  stdF2: "MFA for all users - included",
  stdF3: "2 databases included",
  stdF4: "No included app server",
  stdF5: "App Servers available to add",
  stdF6: "VPC auto-added with first app server",
  advTag: "Tiered Pricing",
  advIntro: "Bundled infrastructure with tier-based pricing - the more users your customer has, the lower their per-user monthly cost. Includes an application server, 128GB DB, VPC and MFA. Minimum 20 users.",
  advF1: "Tiered PUPM (min. 20 users)",
  advF2: "MFA for all users - included",
  advF3: "App Server MO 2vCPU/16GB",
  advF4: "HANA/SQL DB 128GB - included",
  advF5: "VPC - included",
  mtpTag: "Tiered + Multi-Tenant",
  mtpIntro: "Everything in Advanced plus the Cloud Control Centre - purpose-built for partners managing SAP B1 across multiple end customers from a single platform. Same tier pricing as Advanced.",
  mtpF1: "All Advanced features included",
  mtpF2: "MFA for all users - included",
  mtpF3: "App Server + HANA/SQL DB",
  mtpF4: "VPC - included",
  mtpF5: "Cloud Control Centre (CCC)",
  cusTag: "Bespoke Build",
  cusIntro: "Fully tailor-made private cloud. No bundled constraints - choose exactly the infrastructure you need. For sub-20 user SAP deployments, non-SAP workloads, or any specific configuration requirement.",
  cusF1: "Any user count (including <20)",
  cusF2: "SAP or non-SAP workloads",
  cusF3: "Any server configuration",
  cusF4: "MFA & white-labelling included",
  cusF5: "Priced per build - see Section 7",

  // Section 2
  s2Title: "2. Platform Bundle - What's Included",
  s2Intro: "Every C4P subscription - Standard, Advanced, MTPro and Custom - includes all of the following in the monthly per-user price. No extras to quote, no surprises at renewal.",
  stat1Lbl: "Uptime SLA",
  stat2Val: "SAP-Expert",
  stat2Lbl: "Support Team",
  stat3Lbl: "Global AWS Regions",
  stat4Lbl: "Years SAP Expertise",
  feat1Title: "Multi-factor Authentication (MFA)",
  feat1Desc: "TOTP authenticator security on every user login. Built in, not bolted on.",
  feat2Title: "Single Sign-On - Okta Identity",
  feat2Desc: "Enterprise SSO via Okta Identity included in every deployment at no extra cost.",
  feat3Title: "Unlimited Support",
  feat3Desc: "Expert SAP B1 support for partners and customers. No tiered packages, no ticket limits.",
  feat4Title: "Upgrade Assistance",
  feat4Desc: "Managed SAP Business One version upgrades - we handle the complexity so your team doesn't have to.",
  feat5Title: "UAT / Test Environment",
  feat5Desc: "Test confidently before anything hits production. Included for every customer as standard.",
  feat6Title: "SQL to HANA Conversion",
  feat6Desc: "Full SQL to HANA database migration included. No separate project charge, no additional cost.",
  feat7Title: "Trial & Demo Systems",
  feat7Desc: "Spin up sandbox environments for pitching, proving and winning new business. Included.",
  feat8Title: "Migration & Managed Services",
  feat8Desc: "Structured lift-and-shift migrations via the Migration Factory. Included, not an add-on.",
  feat9Title: "AWS WorkSpaces Managed Desktop",
  feat9Desc: "Managed Windows desktop for SAP B1 access from any device, anywhere.",
  feat10Title: "Continuous Performance Optimisation",
  feat10Desc: "Always-on platform tuning. Infrastructure that improves automatically over time.",
  feat11Title: "Infrastructure Generation Upgrades",
  feat11Desc: "Automatic access to next-generation SAP-certified AWS infrastructure as it is released.",
  feat12Title: "SAP-certified Infrastructure",
  feat12Desc: "Architecture 4.0 - the world's first SAP-certified next-gen AWS infrastructure designed for SAP B1.",
  feat13Title: "Data Sovereignty",
  feat13Desc: "Customer data stays within their chosen AWS region. Full sovereignty options across all 9 regions.",
  feat14Title: "Application Servers & VPN",
  feat14Desc: "Available on every product including Standard - no step-up to a different tier required.",
  feat15Title: "Flexible Contract Terms",
  feat15Desc: "Monthly rolling or fixed terms from 1 to 36 months. No lock-in required.",
  wlTitle: "White-labelling - Advanced, MTPro & Custom",
  wlDesc: "Partners on C4P Advanced, MTPro and Custom can white-label the platform and present it under their own brand to customers. Not available on Standard.",

  // Section 3
  s3Title: "3. Per-User Per-Month (PUPM) Pricing",
  s3Intro: "All pricing shown below is for the selected region and currency. All costs are monthly recurring charges.",
  s3StdLabel: "C4P Standard - Flat Rate",
  s3PerUserMo: "per user / month",
  s3NoMin: "Same rate regardless of user count. No minimum.",
  s3AdvLabel: "C4P Advanced & MTPro - Tiered (min. 20 users)",
  colTier: "Tier",
  colUserRange: "User Range",
  colMonthly: "Monthly",

  // Section 4
  s4Title: "4. Database Server Upgrades & Instances",
  s4Callout: "Advanced & MTPro include a 128GB HANA or SQL Server database at no charge. The upgrade cost uses a 2D matrix - server memory tier × user count tier. More users = lower upgrade cost.",
  s4CalloutStatic: "Advanced & MTPro include a 128GB HANA or SQL Server database at no charge. Upgrades to larger servers are available at a fixed monthly price - the same rate regardless of user count.",
  hanaSubtitle: "SAP HANA Upgrades",
  sqlSubtitle: "SQL Server Upgrades",
  colServer: "Server",
  colCpuRam: "CPU / RAM",
  colDataBackup: "Data + Backup",
  hanaIncludedNote: "Included in Advanced & MTPro",
  hanaUpgradeNote: "Upgrade from included base",
  sqlIncludedNote: "Included in Advanced & MTPro",
  sqlUpgradeNote: "Upgrade from included base",
  byolHana: "SAP HANA Licensing: Bring Your Own Licence (BYOL) - you are responsible for the SAP HANA licence and you sell it directly to your customer, keeping the licence revenue. The prices above cover infrastructure only.",
  byolSql: "SQL Server Licensing: Bring Your Own Licence (BYOL) - the partner is responsible for providing and managing the Microsoft SQL Server licence. The prices above cover infrastructure only.",
  dbAllowTitle: "How Many Databases Can You Run?",
  dbAllowSubtitle: "C4P Standard includes 2 database instances in the subscription. Advanced & MTPro include a dedicated 128GB server. Additional databases can be purchased on Standard at a flat monthly rate per database.",
  dbStdColTitle: "C4P Standard",
  dbStdColIntro: "2 database instances are included: one production database and one UAT/test database, both fully managed by Cloud4Partners. Additional databases can be added at a flat monthly rate.",
  dbHanaAllowTitle: "2 Databases Included",
  dbHanaAllowDesc: "1 × Production database + 1 × UAT/test database. Both are fully managed, monitored and backed up by Cloud4Partners as part of the Standard subscription.",
  dbSqlAllowTitle: "Additional Databases Available",
  dbSqlAllowDesc: "Need more than 2 databases? Additional instances can be purchased at a flat monthly rate per database — applies to both HANA and SQL configurations.",
  dbAdvColTitle: "C4P Advanced & MTPro",
  dbAdvColIntro: "The allowance is determined by your chosen server memory. Upgrade the server to unlock more database capacity.",
  dbHanaAdvTitle: "SAP HANA - server memory minus 40 GB core",
  dbHanaAdvDesc: "40 GB is reserved for Core SAP B1. The remaining memory is your customer database space:",
  dbHanaAdvAvail: "GB available",
  dbSqlAdvTitle: "Microsoft SQL - full data drive available",
  dbSqlAdvDesc: "The entire data volume on your SQL server is available for customer databases. See server specs in the table above for included data storage.",

  // Section 5
  s5Title: "5. Application Servers",
  s5InclTitle: "Included (Advanced & MTPro)",
  s5InclDesc: "One MO 2vCPU/16GB Windows server is included at no extra cost. 100GB SSD root volume included.",
  s5StdTitle: "Standard - Add as Needed",
  s5StdDesc: "Application servers are not included in Standard but can be added at the same regional monthly rates. A VPC is automatically added (once) when the first app server is added to a Standard quote.",
  s5InclTag: "Included in Advanced & MTPro",
  colSpec: "Specification",
  colRootVol: "Root Vol.",
  s5Footnote: "Prices are fixed for this region - no FX conversion. Linux, Ubuntu Pro and GPU-optimised variants available at the same regional pricing.",

  addDbTitle: "Additional Database — C4P Standard",
  addDbDesc: "Adds a further database instance to a C4P Standard subscription, beyond the 2 included at no charge. Each additional database is fully managed, monitored and backed up by Cloud4Partners under the same service terms as the included databases.",

  // Section 6
  s6Title: "6. Add-ons & Optional Extras",
  vpnTitle: "VPN Connectivity",
  vpnDesc: "Secure VPN tunnel between your on-premise network and AWS. Used for connecting to on-site systems such as scanners, on-premise servers and network-attached devices. Note: printing does not require a VPN - it is handled through AWS WorkSpaces Advanced Printing, which is included in the platform. (VPC is different - it provides AWS network isolation and is included in Advanced/MTPro.)",
  mfaTitle: "MFA - Multi-Factor Authentication",
  mfaDesc: "Included in all products at no extra cost. Protects every user login with a TOTP authenticator app.",
  wcTitle: "Web Client & Mobile Users",
  wcDesc: "Flat per-user monthly additions for browser or mobile SAP B1 access. These do not count towards the Advanced/MTPro tier pricing calculation.",
  cccTitle: "Cloud Control Centre - MTPro only",
  cccDesc: "Exclusive to C4P MTPro. A management console to provision, monitor and manage multi-tenant SAP B1 environments.",
  wcUserLabel: "Web Client User",
  wcUserDesc: "SAP B1 via browser - does not count towards tier pricing",
  mobUserLabel: "Mobile User",
  mobUserDesc: "SAP B1 on mobile - does not count towards tier pricing",
  addonsFootnote: "Web Client and Mobile users can be added to any product. Pricing is flat - the same rate regardless of user count.",
  colUserType: "User Type",
  colDescription: "Description",

  // Section 7 (C4P Custom)
  s7CustomTitle: "7. C4P Custom - Bespoke Private Cloud Builds",
  s7CustomIntro: "C4P Custom is a fully tailor-made cloud hosting service. There are no bundled constraints - the infrastructure is designed around the specific workload, user count and configuration required. It operates more like Infrastructure-as-a-Service (IaaS): you choose exactly what you need.",
  cusWhenTitle: "When to use C4P Custom",
  cusWhen1: "User count below 20 — Standard includes 2 databases but no dedicated server; Advanced requires 20+ users. Custom provides a fully private dedicated environment for any user count, including smaller servers not available in Standard or Advanced.",
  cusWhen2: "Non-SAP workloads - host any application on AWS. C4P Custom is not limited to SAP Business One.",
  cusWhen3: "Specific infrastructure requirements - unique server configurations, smaller memory options, or builds not available in standard tiers.",
  cusWhen4: "Any topology - multiple databases, no database, application-only builds, or a VPC-only network environment.",
  cusPricingTitle: "How Custom pricing works",
  cusPricing1: "User licence (PUPM) - flat per-user monthly rate, same for all user counts.",
  cusPricing2: "Infrastructure - priced per component. Each server, VPC and add-on is individually quoted. Nothing is bundled.",
  cusPricing3: "Platform bundle - all features from Section 2 apply (MFA, SSO, support, white-labelling, etc.).",
  cusPricing4: "No minimum user count. A single-user Custom build is valid if the requirement calls for it.",
  cusDbTitle: "Custom-only database servers",
  cusDbIntro: "The following servers are available exclusively in C4P Custom builds. They are not part of the Advanced or MTPro upgrade tiers.",
  cusExampleTitle: "Worked Example - Small Private SAP B1 Environment",
  cusExampleIntro: "A customer with 5 SAP Business One users needs a private dedicated environment. Standard does not include a dedicated database server; Advanced requires a minimum of 20 users. C4P Custom is the right solution.",
  cusExampleLabel: "5 users, 64GB HANA Server, Application Server + VPC",
  cusContactPsm: "Contact your PSM for current pricing",
  cusExampleNote: "Custom builds are built to specification - components above are an example configuration only. Contact your Partner Success Manager to discuss and quote a C4P Custom build.",

  // Section 8 (was 7)
  s7Title: "8. How Your Monthly Cost is Calculated",
  s7Intro: "Your monthly total is built from a small number of straightforward components. There are no hidden charges, no annual lump sums, and no FX surprises - the price you see is fixed for your region and currency.",
  step1Title: "Core User Subscription (PUPM)",
  step1Desc: "Multiply your licensed user count by the per-user monthly rate. For Standard, every user costs the same flat rate. For Advanced and MTPro, the per-user rate decreases as your user count grows - all users are billed at the same tier rate.",
  step2Title: "Database Server (Advanced & MTPro)",
  step2Desc: "A 128GB SAP HANA or SQL Server database is included at no extra cost in Advanced & MTPro. If you need more memory, an upgrade is available at a fixed monthly charge. For Standard, 2 database instances (production and UAT) are included. Additional databases can be purchased at a flat monthly rate.",
  step3Title: "Application Servers",
  step3Desc: "Advanced and MTPro include one application server. Additional servers can be added at a fixed monthly fee. Standard customers can add application servers at the same regional rates - a VPC is automatically included with the first server.",
  step4Title: "Optional Extras",
  step4Desc: "VPN connections, additional storage, Web Client users and Mobile users are flat monthly additions. MFA, Okta SSO, support, upgrade assistance, UAT environments and migration services are all included at no extra cost.",
  workedExamplesLabel: "Worked Examples",
  ex1Title: "Example 1 - C4P Standard, 12 users, 1 App Server",
  ex1Term: "12-month term",
  ex1L1Suffix: "flat PUPM",
  ex1L2: "Additional App Server - GP 4vCPU/16GB",
  ex1L3: "VPC (auto-added with app server)",
  exTotal: "Monthly Total (ex-tax)",
  ex2Title: "Example 2 - C4P Advanced, 35 users (Tier 1), HANA 256GB upgrade, 1 extra App Server",
  ex2Term: "24-month term",
  ex2L1Suffix: "Tier 1",
  ex2I1: "App Server MO 2vCPU/16GB - included at no cost",
  ex2I2: "HANA 128GB base - included at no cost",
  ex2I3: "MFA - included   \u2713 VPC - included",
  ex2L2: "HANA 256GB upgrade (Tier 1 rate)",
  ex2L3: "Extra App Server - GP 4vCPU/16GB",
  ex3Title: "Example 3 - C4P MTPro, 60 users (Tier 2), 2 extra App Servers",
  ex3Term: "36-month term",
  ex3L1Suffix: "Tier 2",
  ex3I1: "App Server MO 2vCPU/16GB - included   \u2713 HANA 128GB - included",
  ex3I2: "MFA - included   \u2713 VPC - included   \u2713 Cloud Control Centre (CCC) - included",
  ex3L2: "Extra App Server #1 - GP 4vCPU/16GB",
  ex3L3: "Extra App Server #2 - GP 4vCPU/16GB",
  exTierNote: "At 50 users (Tier 2): user rate drops - HANA upgrade cost also reduces.",

  // Section 8
  s8Title: "9. Frequently Asked Questions",
  faq1Q: "What happens when a customer moves from one pricing tier to the next?",
  faq1A: "The pricing adjusts to the new tier rate from that month's billing. All users are billed at the new lower rate - the tier always applies to the total user count, so moving to a higher tier means a lower per-user cost for everyone.",
  faq2Q: "Can users be added or removed mid-contract?",
  faq2A: "Yes. User counts can be adjusted at any time. Additions are billed pro-rata from the date of change; reductions take effect from the next billing period. Your PSM handles all user count changes.",
  faq3Q: "Are there any setup or onboarding fees?",
  faq3A: "No. All C4P products are subscription-only - there are no setup, migration or onboarding fees. Your first monthly invoice begins on the agreed go-live date.",
  faq4Q: "What is the minimum contract term?",
  faq4A: "The standard minimum contract term is 12 months. Longer terms (24 or 36 months) are available and provide pricing stability for the duration of the contract.",
  faq5Q: "If we're quoting in GBP but the customer is hosted in US East, can we still use GBP?",
  faq5A: "Yes. The hosting region and the billing currency are independent. Your PSM can confirm the applicable rate for any region and currency combination - all combinations are fixed with no FX conversion applied.",
  faq6Q: "What's the difference between a VPN and a VPC?",
  faq6A: "A VPC (Virtual Private Cloud) is AWS network isolation - it keeps the customer's environment secure and separate within AWS. It is included in Advanced and MTPro. A VPN (Virtual Private Network) is a secure tunnel between the customer's on-premise network and AWS, used for connecting to on-site systems such as scanners and on-premise servers. Note: printing does not require a VPN - it is handled through AWS WorkSpaces Advanced Printing, which is included in the platform.",
  faq7Q: "Can a Standard customer upgrade to Advanced or MTPro later?",
  faq7A: "Yes. Upgrades are fully supported. Your PSM will manage the configuration change and updated pricing. The customer benefits from the included infrastructure immediately upon upgrade.",
  faq8Q: "What does monthly pricing mean on a 36-month contract?",
  faq8A: "All C4P pricing is stated as a monthly recurring charge. On a 36-month contract, the quoted monthly amount is invoiced each month for the term. There is no annual lump sum or discounted upfront payment - the monthly rate applies consistently for the full contract duration.",
  faq9Q: "Are prices fixed for the duration of the contract term?",
  faq9A: "Pricing is agreed at the time of contract and applies for the agreed term. Any changes to the subscription configuration (adding users, upgrading DB, adding servers, etc.) are priced at the then-current published rates for the relevant region and currency.",

  // Footer
  footerNote: "All prices shown for the selected region and currency. Download the PDF to share with partners.",
};

export const LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'it', label: 'Italiano',   flag: '🇮🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pt', label: 'Português',  flag: '🇵🇹' },
];
